// API Call Protection System
export class APICallProtector {
  private static instance: APICallProtector;
  private callCounts: Map<string, { count: number; resetTime: number }> = new Map();
  private circuitBreaker: Map<string, { isOpen: boolean; lastFailure: number; failureCount: number }> =
    new Map();

  // Limits
  private readonly MAX_CALLS_PER_MINUTE = 50; // Max calls per minute per component instance
  private readonly MAX_CALLS_PER_SESSION = 100; // Max calls per session
  private readonly CIRCUIT_BREAKER_THRESHOLD = 5; // Failures before opening circuit
  private readonly CIRCUIT_BREAKER_TIMEOUT = 60000; // 1 minute circuit breaker timeout

  static getInstance(): APICallProtector {
    if (!APICallProtector.instance) {
      APICallProtector.instance = new APICallProtector();
    }
    return APICallProtector.instance;
  }

  canMakeCall(componentId: string): { allowed: boolean; reason?: string } {
    const now = Date.now();
    const minuteKey = `${componentId}_minute`;
    const sessionKey = `${componentId}_session`;

    // Check circuit breaker
    const circuit = this.circuitBreaker.get(componentId);
    if (circuit?.isOpen) {
      if (now - circuit.lastFailure < this.CIRCUIT_BREAKER_TIMEOUT) {
        return { allowed: false, reason: 'Circuit breaker is open. Too many failures.' };
      } else {
        // Reset circuit breaker
        this.circuitBreaker.delete(componentId);
      }
    }

    // Check per-minute limit
    const minuteCount = this.callCounts.get(minuteKey);
    if (minuteCount) {
      if (now < minuteCount.resetTime) {
        if (minuteCount.count >= this.MAX_CALLS_PER_MINUTE) {
          return {
            allowed: false,
            reason: 'Por favor contacte con soporte tecnico, has excedido el limite de llamadas por minuto'
          };
        }
      } else {
        // Reset minute counter
        this.callCounts.set(minuteKey, { count: 0, resetTime: now + 60000 });
      }
    } else {
      this.callCounts.set(minuteKey, { count: 0, resetTime: now + 60000 });
    }

    // Check session limit
    const sessionCount = this.callCounts.get(sessionKey);
    if (sessionCount && sessionCount.count >= this.MAX_CALLS_PER_SESSION) {
      return {
        allowed: false,
        reason: 'Por favor contacte con soporte tecnico, has excedido el limite de llamadas por sesion'
      };
    }

    return { allowed: true };
  }

  recordCall(componentId: string) {
    const now = Date.now();
    const minuteKey = `${componentId}_minute`;
    const sessionKey = `${componentId}_session`;

    // Increment minute counter
    const minuteCount = this.callCounts.get(minuteKey);
    if (minuteCount) {
      minuteCount.count++;
    }

    // Increment session counter
    const sessionCount = this.callCounts.get(sessionKey);
    if (sessionCount) {
      sessionCount.count++;
    } else {
      this.callCounts.set(sessionKey, { count: 1, resetTime: now + 24 * 60 * 60 * 1000 }); // 24 hours
    }
  }

  recordFailure(componentId: string) {
    const circuit = this.circuitBreaker.get(componentId) || {
      isOpen: false,
      lastFailure: 0,
      failureCount: 0
    };
    circuit.failureCount++;
    circuit.lastFailure = Date.now();

    if (circuit.failureCount >= this.CIRCUIT_BREAKER_THRESHOLD) {
      circuit.isOpen = true;
      console.warn(`Circuit breaker opened for component ${componentId} due to repeated failures`);
    }

    this.circuitBreaker.set(componentId, circuit);
  }

  // Method to reset all limits for a component (useful for testing or manual reset)
  resetLimits(componentId: string) {
    const minuteKey = `${componentId}_minute`;
    const sessionKey = `${componentId}_session`;
    this.callCounts.delete(minuteKey);
    this.callCounts.delete(sessionKey);
    this.circuitBreaker.delete(componentId);
  }
}

// Function to generate UUID v4 for session token
export const generateUUID = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0,
      v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};
